import { BASE_URL } from '../components/AppConfig';

const getAuthToken = () => {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return sessionToken ? `Bearer ${sessionToken}` : '';
};

const authFetch = async (path, options = {}) => {
  const token = getAuthToken();
  if (!token) {
    return { statusCode: '50000', message: 'Not authenticated' };
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(options.headers || {}),
      },
      ...options,
    });
    return res.json().catch(() => ({}));
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

export const isStoreApiSuccess = (res) =>
  String(res?.statusCode || '') === '10000' || res?.statusCode == null;

export const getStoreApiPayload = (res) => res?.data ?? null;

/** GET /admin/store/settings */
export const getStoreSettings = () => authFetch('/admin/store/settings', { method: 'GET' });

/** PATCH /admin/store/settings */
export const patchStoreSettings = (body) =>
  authFetch('/admin/store/settings', { method: 'PATCH', body: JSON.stringify(body) });

/** POST /admin/store/hubble/sync-brands */
export const syncHubbleBrands = () =>
  authFetch('/admin/store/hubble/sync-brands', { method: 'POST' });

/** POST /admin/store/hubble/sync-discounts */
export const syncHubbleDiscounts = () =>
  authFetch('/admin/store/hubble/sync-discounts', { method: 'POST' });

/** GET /admin/store/hubble/wallet */
export const getHubbleWallet = () => authFetch('/admin/store/hubble/wallet', { method: 'GET' });

/** GET /admin/store/hubble/discounts */
export const getHubbleDiscounts = ({ limit = 100, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return authFetch(`/admin/store/hubble/discounts?${params.toString()}`, { method: 'GET' });
};

/** PATCH /admin/store/hubble/discounts/:brandId — `overriden_discount: null` clears the override. */
export const patchHubbleDiscount = (brandId, body) =>
  authFetch(`/admin/store/hubble/discounts/${brandId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** GET /admin/store/products */
export const getStoreProducts = ({ limit = 100, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return authFetch(`/admin/store/products?${params.toString()}`, { method: 'GET' });
};

/** POST /admin/store/products */
export const createStoreProduct = (body) =>
  authFetch('/admin/store/products', { method: 'POST', body: JSON.stringify(body) });

/** PATCH /admin/store/products/:id */
export const patchStoreProduct = (id, body) =>
  authFetch(`/admin/store/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** PATCH /admin/store/products/reorder — product_ids[0] → sort_order 0, next → 1, … */
export const reorderStoreProducts = (productIds) =>
  authFetch('/admin/store/products/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ product_ids: productIds }),
  });

/** GET /admin/store/orders */
export const getStoreOrders = ({ status, productType, limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('status', status);
  if (productType) params.set('productType', productType);
  return authFetch(`/admin/store/orders?${params.toString()}`, { method: 'GET' });
};

/** PATCH /admin/store/orders/:id */
export const patchStoreOrder = (id, body) =>
  authFetch(`/admin/store/orders/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** POST /admin/store/orders/:id/refund */
export const refundStoreOrder = (id, body = {}) =>
  authFetch(`/admin/store/orders/${id}/refund`, { method: 'POST', body: JSON.stringify(body) });

/** GET /admin/store/categories */
export const getStoreCategories = () => authFetch('/admin/store/categories', { method: 'GET' });

/** POST /admin/store/categories */
export const createStoreCategory = (body) =>
  authFetch('/admin/store/categories', { method: 'POST', body: JSON.stringify(body) });
